import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { ScrollFade } from 'designSystem/components/ScrollFade'
import { Text } from 'designSystem/components/Text'
import { DeferPageMetadataDialog } from 'features/knowledgeDocumentViewer/components/metadata/DeferPageMetadataDialog'
import { EditableMetadataFieldList } from 'features/knowledgeDocumentViewer/components/metadata/EditableMetadataFieldList'
import { EvalQueueNavigation } from 'features/knowledgeDocumentViewer/components/metadata/EvalQueueNavigation'
import { metadataFillLayout } from 'features/knowledgeDocumentViewer/components/metadata/metadataFillLayout'
import {
  BannerIcon,
  ConfirmBanner,
  EvalStatusRow,
} from 'features/knowledgeDocumentViewer/components/metadata/pageMetadataEditSectionStyles'
import { PageMetadataEvalComments } from 'features/knowledgeDocumentViewer/components/metadata/PageMetadataEvalComments'
import { PageMetadataEvalDeferReason } from 'features/knowledgeDocumentViewer/components/metadata/PageMetadataEvalDeferReason'
import { PageMetadataEvalLabeledByBadge } from 'features/knowledgeDocumentViewer/components/metadata/PageMetadataEvalLabeledByBadge'
import { PageMetadataEvalStatusChip } from 'features/knowledgeDocumentViewer/components/metadata/PageMetadataEvalStatusChip'
import { ShouldHaveMetadataField } from 'features/knowledgeDocumentViewer/components/metadata/ShouldHaveMetadataField'
import type { DocumentPageMetadataUserEditComment } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { EvalPageTrailDecision } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalTrail'
import type { MetadataDisplayField } from 'features/knowledgeDocumentViewer/utils/pageMetadataFields'
import React, { useCallback, useState } from 'react'

export type PageMetadataEvalChromeProps = {
  isLoading: boolean
  isSaving: boolean
  isSampling: boolean
  canConfirm: boolean
  canGoBack: boolean
  canGoForward: boolean
  pageStatus: EvalPageTrailDecision
  generatedValuesAreEmpty: boolean
  shouldHaveMetadata: boolean | undefined
  editableFields: readonly MetadataDisplayField[]
  onFieldChange: (label: string, value: string) => void
  onShouldHaveMetadataChange: (value: boolean) => void
  comments: readonly DocumentPageMetadataUserEditComment[]
  commentText: string
  canAddComment: boolean
  onCommentTextChange: (value: string) => void
  onAddComment: () => void
  onConfirm: () => void
  onDefer: (reason: string) => void | Promise<void>
  onPrevious: () => void
  onNext: () => void
  deferReason?: string
  labeledBy?: string
  pageIsOnScreen: boolean
}

export function PageMetadataEvalChrome({
  isLoading,
  isSaving,
  isSampling,
  canConfirm,
  canGoBack,
  canGoForward,
  pageStatus,
  generatedValuesAreEmpty,
  shouldHaveMetadata,
  editableFields,
  onFieldChange,
  onShouldHaveMetadataChange,
  comments,
  commentText,
  canAddComment,
  onCommentTextChange,
  onAddComment,
  onConfirm,
  onDefer,
  onPrevious,
  onNext,
  deferReason,
  labeledBy,
  pageIsOnScreen,
}: PageMetadataEvalChromeProps): React.ReactElement {
  const { t } = useLingui()
  const [isDeferOpen, setIsDeferOpen] = useState(false)
  const controlsDisabled =
    isSaving || isSampling || isLoading || !pageIsOnScreen

  const handleOpenDefer = useCallback(() => {
    setIsDeferOpen(true)
  }, [])

  const handleDefer = useCallback(
    async (reason: string) => {
      await onDefer(reason)
      setIsDeferOpen(false)
    },
    [onDefer]
  )

  return (
    <Box display='flex' direction='column' gap='md' {...metadataFillLayout}>
      <EvalStatusRow>
        <Text as='h3' variant='body' weight='semibold'>
          {t`Page Details`}
        </Text>
        <PageMetadataEvalStatusChip status={pageStatus} />
      </EvalStatusRow>
      {labeledBy === undefined ? null : (
        <PageMetadataEvalLabeledByBadge labeledBy={labeledBy} />
      )}
      <ScrollFade>
        <EditableMetadataFieldList
          fields={editableFields}
          disabled={controlsDisabled}
          onFieldChange={onFieldChange}
        />
      </ScrollFade>
      {generatedValuesAreEmpty ? (
        <ShouldHaveMetadataField
          value={shouldHaveMetadata}
          disabled={controlsDisabled}
          onChange={onShouldHaveMetadataChange}
        />
      ) : null}
      {deferReason === undefined ? null : (
        <PageMetadataEvalDeferReason reason={deferReason} />
      )}
      <PageMetadataEvalComments
        comments={comments}
        commentText={commentText}
        canAddComment={canAddComment}
        disabled={controlsDisabled}
        onCommentTextChange={onCommentTextChange}
        onAddComment={onAddComment}
      />
      <ConfirmBanner role='status'>
        <BannerIcon size={16} aria-hidden />
        <Text variant='body'>
          {t`Confirm the full page, or defer with a reason.`}
        </Text>
      </ConfirmBanner>
      <Box
        display='flex'
        align='center'
        gap='sm'
        wrap='nowrap'
        shrink={false}
        width='full'>
        <Box flex='one' minWidth='none'>
          <Button
            type='button'
            variant='primaryRaised'
            size='lg'
            width='full'
            disabled={controlsDisabled || !canConfirm}
            aria-label={t`Confirm page metadata`}
            onClick={onConfirm}>
            {isSaving ? t`Saving…` : t`Confirm`}
          </Button>
        </Box>
        <Box flex='one' minWidth='none'>
          <Button
            type='button'
            variant='inputRaised'
            size='lg'
            width='full'
            disabled={controlsDisabled}
            aria-label={t`Defer page metadata`}
            onClick={handleOpenDefer}>
            {t`Defer`}
          </Button>
        </Box>
        <EvalQueueNavigation
          canGoBack={canGoBack}
          canGoForward={canGoForward && !isSampling}
          isBusy={isSaving}
          onPrevious={onPrevious}
          onNext={onNext}
        />
      </Box>
      <DeferPageMetadataDialog
        open={isDeferOpen}
        isSaving={isSaving}
        onOpenChange={setIsDeferOpen}
        onDefer={handleDefer}
      />
    </Box>
  )
}
